import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Star, ThumbsUp, MessageSquare } from 'lucide-react';

export default function ReviewsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [filter, setFilter] = useState('all');

  // Mock reviews data
  const mockReviews = [
    {
      id: 1,
      productId: 1,
      productName: 'Yazlık Çiçek Desenli Elbise',
      productImage: 'https://images.unsplash.com/photo-1766193229693-02a4850244af?crop=entropy&cs=srgb&fm=jpg&q=85',
      rating: 5,
      date: '2025-01-10',
      comment: 'Harika bir ürün! Kalitesi çok iyi, beğendim. Herkese tavsiye ederim.',
      likes: 12,
      isLiked: false,
    },
    {
      id: 2,
      productId: 2,
      productName: 'Denim Ceket Streetwear',
      productImage: 'https://images.unsplash.com/photo-1766193229847-1b7331a16d39?crop=entropy&cs=srgb&fm=jpg&q=85',
      rating: 4,
      date: '2025-01-08',
      comment: 'Güzel bir ceket, sadece bedeni biraz büyük geldi.',
      likes: 8,
      isLiked: true,
    },
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <div className="bg-white rounded-xl p-12 text-center max-w-md">
          <Star className="w-24 h-24 mx-auto text-gray-300 mb-6" />
          <h2 className="font-manrope font-bold text-2xl mb-4">Giriş Yapın</h2>
          <p className="text-gray-600 mb-8">Değerlendirmelerinizi görüntülemek için giriş yapmanız gerekiyor.</p>
          <Link
            to="/giris"
            className="inline-block bg-trendyol-orange hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
          >
            Giriş Yap
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 md:py-12" data-testid="reviews-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-manrope font-bold text-2xl md:text-3xl">Değerlendirmelerim</h1>
          <Link
            to="/hesabim"
            className="text-trendyol-orange hover:underline font-medium"
          >
            Hesabıma Dön
          </Link>
        </div>

        <div className="bg-white rounded-xl p-6 mb-6">
          <div className="flex gap-4 overflow-x-auto">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                filter === 'all'
                  ? 'bg-trendyol-orange text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="filter-all"
            >
              Tümü ({mockReviews.length})
            </button>
            <button
              onClick={() => setFilter('5stars')}
              className={`px-6 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                filter === '5stars'
                  ? 'bg-trendyol-orange text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="filter-5stars"
            >
              5 Yıldız
            </button>
            <button
              onClick={() => setFilter('4stars')}
              className={`px-6 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                filter === '4stars'
                  ? 'bg-trendyol-orange text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="filter-4stars"
            >
              4 Yıldız
            </button>
          </div>
        </div>

        {mockReviews.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <MessageSquare className="w-24 h-24 mx-auto text-gray-300 mb-6" />
            <h2 className="font-manrope font-bold text-2xl mb-4">Henüz Değerlendirme Yapmadınız</h2>
            <p className="text-gray-600 mb-8">
              Satın aldığınız ürünler hakkında değerlendirme yaparak diğer müşterilere yardımcı olun.
            </p>
            <Link
              to="/siparislerim"
              className="inline-block bg-trendyol-orange hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Siparişlerime Git
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {mockReviews.map((review) => (
              <div
                key={review.id}
                className="bg-white rounded-xl p-6"
                data-testid={`review-${review.id}`}
              >
                <div className="flex gap-4">
                  <Link to={`/urun/${review.productId}`}>
                    <img
                      src={review.productImage}
                      alt={review.productName}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  </Link>

                  <div className="flex-1">
                    <Link
                      to={`/urun/${review.productId}`}
                      className="font-semibold text-lg hover:text-trendyol-orange transition-colors block mb-2"
                    >
                      {review.productName}
                    </Link>

                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-5 h-5 ${
                              i < review.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-600">
                        {new Date(review.date).toLocaleDateString('tr-TR')}
                      </span>
                    </div>

                    <p className="text-gray-700 mb-4">{review.comment}</p>

                    <div className="flex items-center gap-4">
                      <button
                        className={`flex items-center gap-2 text-sm ${
                          review.isLiked ? 'text-trendyol-orange' : 'text-gray-600'
                        } hover:text-trendyol-orange transition-colors`}
                        data-testid={`like-review-${review.id}`}
                      >
                        <ThumbsUp className={`w-4 h-4 ${review.isLiked ? 'fill-current' : ''}`} />
                        <span>Faydalı ({review.likes})</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}